/**
 * Music Import modal — Update #96 (PS spec deliverables 11-15).
 *
 * 3 tabs:
 *   - Upload files         — drag-drop / picker → per-file track assignment + progress bars
 *   - Royalty-free library — Jamendo / Pixabay / Freesound sub-tabs + genre filter pills
 *   - AI generate          — Mubert (deferred per #93)
 *
 * On success the modal calls `onAddClips([...newClips])` so the parent
 * Production Suite component drops them onto the selected track at the next
 * available slot.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  X, Music, Upload as UploadIcon, Library, Sparkles,
  Loader2, Play as PlayIcon, Pause as PauseIcon, Plus,
  FileAudio, ExternalLink,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import s from "../../pages/ProductionSuite.module.css";
import { appConfirm } from "@/components/AppDialogHost";

const ACCEPT = "audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/aiff,audio/x-aiff,audio/flac,audio/x-flac,audio/m4a,audio/x-m4a,audio/ogg,.mp3,.wav,.aiff,.aif,.flac,.m4a,.ogg";
// Update #159 — match the backend's 250 MB cap. Previously the frontend
// silently rejected any file over 50 MB, which killed lossless WAV /
// FLAC uploads before they even reached the server ("where is Doja Cat
// (2 songs)?"). Now high-res masters flow through as intended.
const MAX_BYTES = 250 * 1024 * 1024;

const GENRES = ["Smooth Jazz", "R&B", "Gospel", "Hip-Hop", "Caribbean", "Dance"];
const SUB_TABS = [
  { id: "jamendo",      label: "Jamendo" },
  { id: "freesound",    label: "Freesound" },
  { id: "pixabay_image", label: "Pixabay" },
];

// Update #144 — cap royalty-free previews at 20s so producers can quickly
// audition a rack of tracks without one full song hijacking the modal.
// The "Add to session" button still uses the full-length URL.
const PREVIEW_CAP_SEC = 20;

function fmtBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDuration(seconds) {
  if (!seconds || seconds <= 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Probe audio duration client-side via <audio>.duration so we don't need ffmpeg server-side
function probeDuration(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    audio.preload = "metadata";
    audio.onloadedmetadata = () => {
      const d = audio.duration;
      URL.revokeObjectURL(url);
      resolve(d && isFinite(d) ? d : 0);
    };
    audio.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
    audio.src = url;
  });
}

export default function PSMusicImportModal({
  open,
  initialTab = "upload",
  tracks,
  selectedTrackId,
  onClose,
  onAddClips,            // (clips: [{name, audioUrl, duration_sec, trackId, color, source}]) => void
}) {
  const [tab, setTab] = useState(initialTab);
  // Update #159 — track how many upload-queue rows have not yet been
  // POSTed. If the user tries to close the modal while some are still
  // "queued", we confirm — a lot of frustrated support tickets ("where
  // is the file I just uploaded?") were actually pending queue items
  // that were discarded when the modal closed.
  const [pendingUploadCount, setPendingUploadCount] = useState(0);
  useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab]);
  // Reset the pending counter every time the modal (re)opens so a stale
  // count from a previous session doesn't fire a spurious confirm.
  useEffect(() => { if (open) setPendingUploadCount(0); }, [open]);

  const safeClose = React.useCallback(async () => {
    if (pendingUploadCount > 0) {
      const yes = await appConfirm(
        `You have ${pendingUploadCount} file${pendingUploadCount === 1 ? "" : "s"} queued but not uploaded.\n\nClick "Add to session" first, or close anyway and discard the queue?`,
      );
      if (!yes) return;
    }
    onClose?.();
  }, [pendingUploadCount, onClose]);

  if (!open) return null;

  return (
    <div className={s.modalOverlay} data-testid="ps-music-import-modal" onClick={safeClose}>
      <div
        className={s.modal}
        style={{ maxWidth: 620 }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={s.modalHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div className={s.modalIcon}><Music size={14} /></div>
            <div>
              <div className={s.modalTitle}>Import music</div>
              <div className={s.modalSubtitle}>Add audio to your session — upload, browse, or generate.</div>
            </div>
          </div>
          <button type="button" className={s.modalClose} onClick={safeClose} data-testid="ps-music-import-close">
            <X size={14} />
          </button>
        </div>

        {/* Tab strip */}
        <div className={s.modalTabs}>
          <ModalTab id="upload"    icon={UploadIcon} active={tab === "upload"}    onClick={setTab}>Upload files</ModalTab>
          <ModalTab id="library"   icon={Library}    active={tab === "library"}   onClick={setTab}>Royalty-free library</ModalTab>
          <ModalTab id="ai"        icon={Sparkles}   active={tab === "ai"}        onClick={setTab}>AI generate</ModalTab>
        </div>

        <div className={s.modalBody}>
          {tab === "upload"  && <UploadTab  tracks={tracks} selectedTrackId={selectedTrackId} onAddClips={onAddClips} onPendingChange={setPendingUploadCount} />}
          {tab === "library" && <LibraryTab tracks={tracks} selectedTrackId={selectedTrackId} onAddClips={onAddClips} />}
          {tab === "ai"      && <AIGenerateTab />}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function ModalTab({ id, icon: Icon, active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      className={`${s.modalTab} ${active ? s.modalTabActive : ""}`}
      data-testid={`ps-music-import-tab-${id}`}
    >
      <Icon size={11} /> {children}
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB 1 — Upload files
// ────────────────────────────────────────────────────────────────────────────
function UploadTab({ tracks, selectedTrackId, onAddClips, onPendingChange }) {
  const [queue, setQueue] = useState([]);         // [{file, trackId, progress, status, audioUrl, duration, error}]
  const [draggingOver, setDraggingOver] = useState(false);
  const fileInputRef = useRef(null);

  // Update #159 — report pending count to the parent modal so it can
  // guard against accidental close-with-queue-loss.
  useEffect(() => {
    if (!onPendingChange) return;
    const pending = queue.filter((q) => q.status !== "done" && q.status !== "error").length;
    onPendingChange(pending);
  }, [queue, onPendingChange]);

  const acceptFiles = async (files) => {
    if (!files || files.length === 0) return;
    const additions = [];
    for (const file of Array.from(files)) {
      if (file.size > MAX_BYTES) {
        toast.error(
          `${file.name} is ${(file.size / (1024*1024)).toFixed(1)} MB — the cap is ${MAX_BYTES / (1024*1024)} MB. Try re-exporting as MP3 or a lower-bitrate WAV.`,
          { duration: 8000 },
        );
        continue;
      }
      const duration = await probeDuration(file);
      additions.push({
        id: `q_${Date.now()}_${additions.length}`,
        file, trackId: selectedTrackId || "bed",
        progress: 0, status: "queued",
        duration, audioUrl: "", error: null,
      });
    }
    setQueue((prev) => [...prev, ...additions]);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDraggingOver(false);
    acceptFiles(e.dataTransfer?.files);
  };

  const uploadOne = async (idx) => {
    setQueue((p) => p.map((q, i) => i === idx ? { ...q, status: "uploading", progress: 5 } : q));
    const q = queue[idx];
    const form = new FormData();
    form.append("file", q.file);
    form.append("track_id", q.trackId);
    form.append("label", q.file.name.replace(/\.[^.]+$/, ""));
    try {
      const r = await api.post("/production/import", form, {
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.min(99, Math.round((evt.loaded / evt.total) * 100));
          setQueue((p) => p.map((qi, i) => i === idx ? { ...qi, progress: pct } : qi));
        },
      });
      const asset = r.data?.asset || {};
      const audioUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + (r.data?.audio_url || asset.audio_url || "");
      setQueue((p) => p.map((qi, i) => i === idx
        ? { ...qi, status: "done", progress: 100, audioUrl, assetId: asset.asset_id }
        : qi));
      return { success: true, clip: {
        asset_id: asset.asset_id, name: asset.label || q.file.name,
        audio_url: audioUrl, duration_sec: q.duration,
        trackId: q.trackId, source: "production_import",
      }};
    } catch (e) {
      let detail = e?.response?.data?.detail || e?.message || "Upload failed";
      if (e?.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text())?.detail || detail; } catch { /* noop */ }
      }
      if (Array.isArray(detail)) detail = detail[0]?.msg || "Upload failed";
      setQueue((p) => p.map((qi, i) => i === idx ? { ...qi, status: "error", error: String(detail) } : qi));
      return { success: false };
    }
  };

  const uploadAll = async () => {
    const successes = [];
    for (let i = 0; i < queue.length; i++) {
      if (queue[i].status === "done") continue;
      const r = await uploadOne(i);
      if (r.success) successes.push(r.clip);
    }
    if (successes.length > 0) {
      onAddClips?.(successes);
      toast.success(`Imported ${successes.length} file${successes.length === 1 ? "" : "s"}`);
      // Update #156 — tell the Asset Library to re-fetch so the new
      // imports appear under the "Uploads" filter immediately.
      try { window.dispatchEvent(new CustomEvent("bfa:library-refresh")); } catch { /* noop */ }
    }
  };

  return (
    <>
      <div
        className={`${s.dropzone} ${draggingOver ? s.dropzoneActive : ""}`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDraggingOver(true); }}
        onDragLeave={() => setDraggingOver(false)}
        onDrop={onDrop}
        data-testid="ps-music-import-dropzone"
      >
        <UploadIcon size={22} color="#4a9eff" />
        <div className={s.dropzoneTitle}>Drag files here or click to browse</div>
        <div className={s.dropzoneSub}>Add directly to any track in the timeline · Up to {MAX_BYTES / (1024*1024)} MB per file</div>
        <div className={s.formatBadges}>
          {["MP3", "WAV", "AIFF", "FLAC"].map((f) => <span key={f} className={s.formatBadge}>{f}</span>)}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          multiple
          style={{ display: "none" }}
          onChange={(e) => acceptFiles(e.target.files)}
          data-testid="ps-music-import-file-input"
        />
      </div>

      {queue.length > 0 && (() => {
        const pending = queue.filter((q) => q.status !== "done" && q.status !== "error").length;
        if (pending === 0) return null;
        return (
          <div
            className={s.queueReadyBanner}
            data-testid="ps-music-import-pending-banner"
            role="status"
          >
            <span className={s.queueReadyBannerDot} />
            <span>
              <strong>{pending} file{pending === 1 ? "" : "s"} ready</strong>
              {" "}— click <strong>Add to session</strong> below to upload. Closing this window now will lose them.
            </span>
          </div>
        );
      })()}

      {queue.length > 0 && (
        <div className={s.queueList} data-testid="ps-music-import-queue">
          {queue.map((q, i) => (
            <div key={q.id} className={s.queueRow}>
              <FileAudio size={14} color="#4a9eff" />
              <div className={s.queueMeta}>
                <div className={s.queueName}>{q.file.name}</div>
                <div className={s.queueDetail}>{fmtBytes(q.file.size)} · {fmtDuration(q.duration)}</div>
                {q.status === "uploading" && (
                  <div className={s.progressBar}><div className={s.progressFill} style={{ width: `${q.progress}%` }} /></div>
                )}
                {q.error && <div className={s.queueError}>{q.error}</div>}
              </div>
              <select
                value={q.trackId}
                onChange={(e) => setQueue((p) => p.map((qi, idx) => idx === i ? { ...qi, trackId: e.target.value } : qi))}
                className={s.queueSelect}
                disabled={q.status === "uploading" || q.status === "done"}
                data-testid={`ps-music-import-track-${i}`}
              >
                {tracks.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <span className={s.queueStatus} data-status={q.status}>
                {q.status === "queued"     && "Queued"}
                {q.status === "uploading"  && `${q.progress}%`}
                {q.status === "done"       && "Done"}
                {q.status === "error"      && "Failed"}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className={s.modalFooter}>
        <div className={s.modalFooterHint}>
          Drag files or browse — assign each file to a track before upload.
        </div>
        <button
          type="button"
          className={s.primaryBtn}
          disabled={queue.length === 0 || queue.every((q) => q.status === "done")}
          onClick={uploadAll}
          data-testid="ps-music-import-submit"
        >
          {queue.some((q) => q.status === "uploading") ? <Loader2 size={12} className={s.spin} /> : <Plus size={12} />}
          Add to session
        </button>
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB 2 — Royalty-Free Library
// ────────────────────────────────────────────────────────────────────────────
function LibraryTab({ tracks, selectedTrackId, onAddClips }) {
  const [subTab, setSubTab] = useState("jamendo");
  const [genre, setGenre] = useState(GENRES[0]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const previewRef = useRef(null);
  const [previewRemaining, setPreviewRemaining] = useState(PREVIEW_CAP_SEC);
  const [trackId, setTrackId] = useState(selectedTrackId || "bed");

  // Update #161 — "Recently searched" chip strip. Remembers the last 3
  // successful {source, genre} combos so producers can hop back to a
  // preferred station library (e.g. "Smooth Jazz · Jamendo") in one
  // click instead of re-selecting the source + genre pills. Persisted
  // in localStorage so the shortcut survives modal close / page reload.
  const RECENT_KEY = "bfa-ps-library-recent";
  const RECENT_LIMIT = 3;
  const [recents, setRecents] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
      return Array.isArray(raw) ? raw.filter((r) => r && r.source && r.genre).slice(0, RECENT_LIMIT) : [];
    } catch {
      return [];
    }
  });
  const pushRecent = React.useCallback((source, genreKey) => {
    setRecents((prev) => {
      const filtered = prev.filter((r) => !(r.source === source && r.genre === genreKey));
      const next = [{ source, genre: genreKey, ts: Date.now() }, ...filtered].slice(0, RECENT_LIMIT);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, []);

  // Single source of truth for stopping any in-flight preview. Used by the
  // unmount cleanup, sub-tab/genre swaps, and the Stop pill button so we
  // never leak an Audio element playing in the background.
  const stopPreview = React.useCallback(() => {
    try {
      const a = previewRef.current;
      if (a) {
        a.pause();
        a.currentTime = 0;
        a.src = "";
      }
    } catch { /* noop */ }
    previewRef.current = null;
    setPreviewId(null);
  }, []);

  // Cleanup on unmount — catches modal close (parent stops rendering this
  // component) AND tab switch back to Upload / AI Generate. Previously audio
  // would keep playing in the background with no way to stop it.
  useEffect(() => stopPreview, [stopPreview]);

  // Listen for the Production Suite's global "stop all preview" broadcast
  // (fired when the user hits Esc anywhere in the DAW). Lets pro producers
  // kill any runaway audio with a single keystroke even if the modal is
  // partially obscured or the cursor is in the wrong region.
  useEffect(() => {
    const onStopAll = () => stopPreview();
    window.addEventListener("bfa:ps-stop-all-preview", onStopAll);
    return () => window.removeEventListener("bfa:ps-stop-all-preview", onStopAll);
  }, [stopPreview]);

  useEffect(() => {
    let cancelled = false;
    // Refreshing the result list — kill any in-flight preview because the
    // current previewId may not exist in the new list.
    stopPreview();
    setLoading(true);
    setItems([]);
    // Update #160 — silent single-retry on transient failures. Prevents
    // the "Search failed" toast from surfacing during a backend restart
    // or a brief network flap; only nags the user if it's still broken
    // ~700ms later.
    const path = `/imaging/library/search?source=${subTab}&genre=${encodeURIComponent(genre)}&limit=12`;
    const runSearch = () => api.get(path);
    runSearch()
      .catch(async (err) => {
        // Bail immediately on legitimate 4xx (auth, bad param) — those
        // won't self-heal on a retry.
        const status = err?.response?.status;
        if (status && status >= 400 && status < 500) throw err;
        if (cancelled) throw err;
        await new Promise((res) => setTimeout(res, 700));
        return runSearch();
      })
      .then((r) => {
        if (cancelled) return;
        const list = r.data?.tracks || [];
        setItems(list);
        // Only remember the search if it actually returned something —
        // there's no point in re-clicking a chip that we know is empty.
        if (list.length > 0) pushRecent(subTab, genre);
      })
      .catch((e) => {
        if (cancelled) return;
        const detail = e?.response?.data?.detail;
        const status = e?.response?.status;
        const label = typeof detail === "string"
          ? detail
          : (status ? `Search failed (HTTP ${status})` : "Search failed — check your connection and try again");
        // Log the full error to the console so pro users / support can
        // see what happened without needing devtools open at the exact
        // moment of the failure.
        console.error("[LibraryTab] search error", e);
        toast.error(label, { duration: 6000 });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
      // Also add pushRecent so the effect refetches when we mutate
      // recents (not strictly needed but keeps the exhaustive-deps rule
      // quiet).
  }, [subTab, genre, stopPreview, pushRecent]);

  const togglePreview = (t) => {
    if (previewId === t.id) {
      stopPreview();
      return;
    }
    // Switching to a different row — stop the previous one cleanly first
    stopPreview();
    const a = new Audio(t.preview_url);
    setPreviewRemaining(PREVIEW_CAP_SEC);
    // Update #144 — cap the preview at 20 s so producers can rapid-audition
    // a shelf of tracks without one long song hijacking the modal. The
    // "Add to session" button still uses the full-length URL, so this is
    // purely an audition cap.
    a.addEventListener("timeupdate", () => {
      const remaining = Math.max(0, PREVIEW_CAP_SEC - a.currentTime);
      setPreviewRemaining(remaining);
      if (a.currentTime >= PREVIEW_CAP_SEC) {
        a.pause();
        a.currentTime = 0;
        // Match the natural-end flow so the Now-Playing bar disappears.
        setPreviewId(null);
        previewRef.current = null;
      }
    });
    a.play().then(() => {
      previewRef.current = a;
      setPreviewId(t.id);
      a.onended = () => setPreviewId(null);
    }).catch(() => toast.error("Preview blocked — try downloading instead"));
  };

  const addToSession = (t) => {
    // Stop the preview before adding so the user doesn't end up with a
    // stale preview playing in the background after the modal closes.
    stopPreview();
    onAddClips?.([{
      name: t.name || `Track ${t.id}`,
      audio_url: t.preview_url || t.download_url,
      duration_sec: t.duration_s || 0,
      trackId, source: subTab,
      external_url: t.external_url,
    }]);
    toast.success(`Added "${(t.name || "").slice(0, 32)}" to session`);
  };

  // Find the currently-previewing track for the Now-Playing bar
  const playingTrack = previewId ? items.find((x) => x.id === previewId) : null;

  return (
    <>
      {recents.length > 0 && (
        <div className={s.recentSearches} data-testid="ps-music-import-recents">
          <span className={s.recentSearchesLabel}>Recent</span>
          {recents.map((r) => {
            const active = r.source === subTab && r.genre === genre;
            return (
              <button
                type="button"
                key={`${r.source}::${r.genre}`}
                onClick={() => { setSubTab(r.source); setGenre(r.genre); }}
                className={`${s.recentSearchChip} ${active ? s.recentSearchChipActive : ""}`}
                data-testid={`ps-music-import-recent-${r.source}-${r.genre.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                title={`Load ${r.genre} from ${r.source}`}
              >
                {r.genre} <span className={s.recentSearchChipSource}>· {r.source}</span>
              </button>
            );
          })}
        </div>
      )}
      <div className={s.subTabs}>
        {SUB_TABS.map((st) => (
          <button type="button" key={st.id}
            onClick={() => setSubTab(st.id)}
            className={`${s.subTab} ${subTab === st.id ? s.subTabActive : ""}`}
            data-testid={`ps-music-import-source-${st.id}`}>{st.label}</button>
        ))}
      </div>
      <div className={s.genrePills} data-testid="ps-music-import-genres">
        {GENRES.map((g) => (
          <button type="button" key={g}
            onClick={() => setGenre(g)}
            className={`${s.genrePill} ${genre === g ? s.genrePillActive : ""}`}>{g}</button>
        ))}
      </div>

      {/* Sticky Now-Playing bar — visible the entire time a preview is in
          flight so the user always has a clear, prominent Stop button no
          matter where they've scrolled to in the result list. */}
      {playingTrack && (
        <div
          className={s.previewNowBar}
          data-testid="ps-music-import-now-playing-bar"
        >
          <PlayIcon size={11} color="#3ecf8e" />
          <div className={s.previewNowMeta}>
            <div className={s.previewNowTitle}>{playingTrack.name}</div>
            <div className={s.previewNowSub}>
              Preview · {Math.ceil(previewRemaining)}s left · {playingTrack.source || subTab}
            </div>
          </div>
          <button
            type="button"
            onClick={stopPreview}
            className={`${s.actionPill} ${s.actionPillPrimary}`}
            data-testid="ps-music-import-stop-all-btn"
          >
            <PauseIcon size={9} /> Stop preview
          </button>
        </div>
      )}

      <div className={s.libraryResults}>
        {loading ? (
          <div style={{ padding: 20, textAlign: "center", color: "#4a5068", fontSize: 11 }}>
            <Loader2 size={14} className={s.spin} /> Searching {SUB_TABS.find((x) => x.id === subTab)?.label}…
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: "#4a5068", fontSize: 11 }}>
            No results. Try a different genre.
          </div>
        ) : items.map((t) => (
          <div key={`${subTab}-${t.id}`} className={s.libraryRow} data-testid={`ps-music-import-item-${t.id}`}>
            <div className={s.libraryDot} style={{ background: "#3ecf8e" }} />
            <div className={s.libraryMeta}>
              <div className={s.libraryName}>{t.name}</div>
              <div className={s.libraryDetail}>
                {fmtDuration(t.duration_s)} · {t.source || subTab}
                {t.license && <> · {String(t.license).slice(0, 38)}</>}
              </div>
            </div>
            {t.external_url && (
              <a href={t.external_url} target="_blank" rel="noreferrer" className={s.libraryExternal}>
                <ExternalLink size={10} />
              </a>
            )}
            <button type="button" onClick={() => togglePreview(t)} className={s.actionPill}
              data-testid={`ps-music-import-preview-${t.id}`}>
              {previewId === t.id ? <PauseIcon size={9} /> : <PlayIcon size={9} />}
              {previewId === t.id ? "Stop" : "Preview"}
            </button>
            <button type="button" onClick={() => addToSession(t)} className={`${s.actionPill} ${s.actionPillPrimary}`}
              data-testid={`ps-music-import-add-${t.id}`}>
              <Plus size={9} /> Add
            </button>
          </div>
        ))}
      </div>

      <div className={s.modalFooter}>
        <div className={s.modalFooterHint}>
          Add to track: 
          <select value={trackId} onChange={(e) => setTrackId(e.target.value)}
            className={s.queueSelect} style={{ marginLeft: 6 }}
            data-testid="ps-music-import-library-track">
            {tracks.map((tr) => <option key={tr.id} value={tr.id}>{tr.name}</option>)}
          </select>
        </div>
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB 3 — AI Generate (deferred per Update #93 scoping)
// ────────────────────────────────────────────────────────────────────────────
function AIGenerateTab() {
  return (
    <div className={s.aiTabPlaceholder} data-testid="ps-music-import-ai-placeholder">
      <Sparkles size={20} color="#a78bfa" />
      <div className={s.placeholderTitle}>AI music generation — coming soon</div>
      <div className={s.placeholderText}>
        Mubert text-to-music and Suno BYOK are on the roadmap. They require
        provider credentials we&apos;re still finalising — once those land
        you&apos;ll be able to generate beds directly from this tab without
        leaving the Production Suite.
      </div>
      <a href="/dashboard/ai-providers" className={s.placeholderLink}>
        Set up an AI provider →
      </a>
    </div>
  );
}
